import React from "react";
import { Box, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CustomDialog from "../CustomDialog";
import CustomButton from "../CustomButton";
import CustomTextField from "../CustomTextField";
import CustomSelect from "../CustomSelect";
import CustomLabel from "../CustomLabel";
import { COLORS } from "../../assets/theme";
import { payCoach } from "../../services/CoachesService";
import { listAccounts, Account } from "../../services/newAccountsServices";
import { getAllBranches } from "../../services/BranchesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { formatMoney } from "./classUtils";
import { Coach, CoachPayout, PayCoachResponse } from "./types";

const ALL_UNITS_VALUE = "all";

const pickNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return undefined;
};

interface CoachPayDialogProps {
  open: boolean;
  coach: Coach | null;
  /** The outstanding-balance payout report (no from/to) — supplies the max payable amount and breakdown. */
  payout: CoachPayout | null;
  onClose: () => void;
  /** Fired after a successful payment, so callers can refresh the outstanding balance. */
  onPaid?: (result: PayCoachResponse) => void;
}

/** Builds a default, editable description from the payout breakdown. */
const defaultDescription = (coach: Coach | null, payout: CoachPayout | null) => {
  const name = coach?.full_name || "coach";
  const sessionCount = payout?.session_count ?? 0;
  const sessionLabel = `${sessionCount} session${sessionCount === 1 ? "" : "s"}`;
  return `Payout for ${name} — ${sessionLabel}`;
};

const CoachPayDialog: React.FC<CoachPayDialogProps> = ({
  open,
  coach,
  payout,
  onClose,
  onPaid,
}) => {
  const { businessId } = useParams<{ businessId?: string }>();

  const [accounts, setAccounts] = React.useState<Account[]>([]);
  const [loadingAccounts, setLoadingAccounts] = React.useState(false);
  const [accountId, setAccountId] = React.useState<string | number>("");
  const [units, setUnits] = React.useState<any[]>([]);
  const [loadingUnits, setLoadingUnits] = React.useState(false);
  const [unitScope, setUnitScope] = React.useState<string>(ALL_UNITS_VALUE);
  const [currency, setCurrency] = React.useState("USD");
  const [amount, setAmount] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const coachId = coach?.id ? String(coach.id) : "";
  const outstanding = Number(payout?.total_payout) || 0;

  const businessDisplayName = React.useMemo(() => {
    const firstUnit = units[0];
    return (
      pickNonEmptyString(
        firstUnit?.business_name,
        firstUnit?.businessName,
        firstUnit?.business?.name,
        firstUnit?.venue_name,
        firstUnit?.venueName
      ) || (businessId ? `Business ${businessId}` : "Business")
    );
  }, [units, businessId]);

  React.useEffect(() => {
    if (!open) return;
    setAmount(outstanding > 0 ? String(outstanding) : "");
    setDescription(defaultDescription(coach, payout));
    setNotes("");
    setCurrency("USD");
    setAccountId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, coachId]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingAccounts(true);
    listAccounts()
      .then((data) => {
        if (cancelled) return;
        const expenseAccounts = (data || []).filter(
          (a: any) => String(a?.code || "").toUpperCase() === "EXPENSE"
        );
        setAccounts(expenseAccounts);
        // Wages are always paid from the Expense account — it's the only
        // one offered, so just pick it instead of making the admin choose.
        setAccountId(expenseAccounts[0]?.id ?? "");
      })
      .catch((error) => {
        if (!cancelled) {
          toast.error(getErrorMessage(error, "Failed to load accounts."));
          setAccounts([]);
          setAccountId("");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingAccounts(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  React.useEffect(() => {
    if (!open || !businessId) return;
    let cancelled = false;
    setLoadingUnits(true);
    getAllBranches(businessId)
      .then((response) => {
        if (cancelled) return;
        let unitsData: any[] = [];
        if (response && response.units) unitsData = response.units;
        else if (response && response.data) unitsData = response.data;
        else if (Array.isArray(response)) unitsData = response;
        const normalizedUnits = Array.isArray(unitsData) ? unitsData : [];
        setUnits(normalizedUnits);
        // Only one real unit — pre-select it instead of leaving "All" selected.
        setUnitScope(
          normalizedUnits.length === 1
            ? String(normalizedUnits[0].id || normalizedUnits[0]._id)
            : ALL_UNITS_VALUE
        );
      })
      .catch((error) => {
        if (!cancelled) {
          toast.error(getErrorMessage(error, "Failed to load units."));
          setUnits([]);
          setUnitScope(ALL_UNITS_VALUE);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingUnits(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, businessId]);

  const handlePay = async () => {
    if (!coachId) return;
    if (!businessId) {
      toast.error("No business context found for this payment.");
      return;
    }
    if (!accountId) {
      toast.error("Select an account to pay from.");
      return;
    }
    if (!currency.trim()) {
      toast.error("Enter a currency.");
      return;
    }

    let parsedAmount: number | undefined;
    if (amount.trim() !== "") {
      parsedAmount = Number(amount);
      if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
        toast.error("Enter a valid, positive amount.");
        return;
      }
      if (outstanding > 0 && parsedAmount > outstanding) {
        toast.error(`Amount can't exceed the outstanding balance (${formatMoney(outstanding)}).`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const result = await payCoach(coachId, {
        accountId: Number(accountId),
        currency: currency.trim(),
        businessId,
        unitId: unitScope === ALL_UNITS_VALUE ? undefined : unitScope,
        amount: parsedAmount,
        description: description.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      toast.success("Payment recorded.");
      onPaid?.(result);
      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to record the payment."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      paperSx={{ minWidth: { sm: 480 }, maxWidth: 560 }}
      title={`Pay — ${coach?.full_name || "Coach"}`}
      actions={
        <>
          <CustomButton
            label="Cancel"
            variant="outlined"
            onClick={onClose}
            disabled={submitting}
          />
          <CustomButton
            label="Confirm Payment"
            variant="contained"
            bgColor={COLORS.MainColor}
            textColor={COLORS.WhiteText}
            loading={submitting}
            onClick={handlePay}
          />
        </>
      }
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 1.5,
          mb: 2,
          borderRadius: 2,
          bgcolor: COLORS.MainBackground,
          border: "1px solid #eee",
        }}
      >
        <Typography sx={{ fontSize: 13, color: "#666" }}>
          Outstanding balance ({payout?.session_count ?? 0} session
          {(payout?.session_count ?? 0) === 1 ? "" : "s"})
        </Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
          {formatMoney(outstanding)}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <CustomTextField
          label="Account"
          value={
            loadingAccounts
              ? "Loading..."
              : accounts[0]
              ? accounts[0].name
              : "No expense account found"
          }
          disabled
          fullWidth
        />

        <Box>
          <CustomLabel text="Unit / Branch" fontSize={14} fontWeight={500} />
          <CustomSelect
            value={unitScope}
            onChange={(v) => setUnitScope(String(v))}
            options={[
              { label: `All (${businessDisplayName})`, value: ALL_UNITS_VALUE },
              ...units.map((u) => ({
                label: u.name || u.title || String(u.id || u._id),
                value: String(u.id || u._id),
              })),
            ]}
            placeholder={loadingUnits ? "Loading units..." : "Select a unit"}
            disabled={loadingUnits}
            fullWidth
          />
        </Box>

        <CustomTextField
          label="Currency"
          value={currency}
          onChange={(e: any) => setCurrency(e.target.value.toUpperCase())}
          fullWidth
        />

        <CustomTextField
          label="Amount"
          type="number"
          value={amount}
          onChange={(e: any) => setAmount(e.target.value)}
          helperText={
            outstanding > 0
              ? `Leave as-is to pay the full outstanding balance, or lower it for a partial payment (max ${formatMoney(
                  outstanding
                )}).`
              : undefined
          }
          fullWidth
        />

        <CustomTextField
          label="Description"
          value={description}
          onChange={(e: any) => setDescription(e.target.value)}
          fullWidth
        />

        <CustomTextField
          label="Notes"
          value={notes}
          onChange={(e: any) => setNotes(e.target.value)}
          multiline
          rows={2}
          fullWidth
        />
      </Box>
    </CustomDialog>
  );
};

export default CoachPayDialog;
